// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: legalPages/$pageId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Admin/founder-managed legal pages (terms of service and privacy policy) for the Danger Dan! project. Stores full page content as markdown/plain text.
 */

export interface LegalPagesRequest {
  pageType: string;
  content: string;
  updatedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface LegalPagesResponse {
  pageType: string;
  content: string;
  updatedAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a LegalPages operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildLegalPages(pageId: string, data: LegalPagesRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `legalPages/${pageId}`, document: doc };
}

/**
 * Admin or founder only. Creates a legal page document with pageType ('terms' or 'privacy'), full content text, and updatedAt timestamp. pageType is immutable after creation. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setLegalPages(pageId: string, data: LegalPagesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`legalPages/${pageId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting LegalPages: ${formatError(error)}`);
    return false;
  }
}

export type LegalPagesRequestUpdate = Partial<LegalPagesRequest>;

/**
 * Build a LegalPages update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateLegalPages(pageId: string, data: LegalPagesRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `legalPages/${pageId}`, document: doc };
}

/**
 * Admin or founder only. Can update content and updatedAt fields to reflect new page revisions. pageType cannot be changed after creation (immutable). (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateLegalPages(pageId: string, data: LegalPagesRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`legalPages/${pageId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating LegalPages: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read legal pages. Used to display terms of service and privacy policy publicly on the site.
   (Get Single Item)
 */
export async function getLegalPages(pageId: string): Promise<LegalPagesResponse | null> {
  try {
    const result = await get(`legalPages/${pageId}`);
    return result as LegalPagesResponse | null;
  } catch (error) {
    console.error(`Error getting LegalPages: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single LegalPages document. (
  Read Operation Details: Anyone can read legal pages. Used to display terms of service and privacy policy publicly on the site.
  )
 */
export function subscribeLegalPages(callback: (data: LegalPagesResponse | null) => void, pageId: string): Promise<() => Promise<void>> {
  return subscribe(`legalPages/${pageId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as LegalPagesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to LegalPages at path '${`legalPages/${pageId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many LegalPages items from collection legalPages
 
  Read Operation Details: Anyone can read legal pages. Used to display terms of service and privacy policy publicly on the site.
  
 */
export async function getManyLegalPages(filter: string = ""): Promise<LegalPagesResponse[]> {
  try {
    const results = await get("legalPages", { prompt: filter });
    return Array.isArray(results) ? results as LegalPagesResponse[] : [];
  } catch (error) {
    console.error(`Error getting LegalPages collection at path 'legalPages': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in LegalPages collection at legalPages
 
  Read Operation Details: Anyone can read legal pages. Used to display terms of service and privacy policy publicly on the site.
  
 */
export function subscribeManyLegalPages(callback: (data: LegalPagesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("legalPages", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as LegalPagesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to LegalPages collection at path '${"legalPages"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all LegalPages items from collection legalPages
 
  Read Operation Details: Anyone can read legal pages. Used to display terms of service and privacy policy publicly on the site.
  
 */
export async function getAllLegalPages(filter: string = ""): Promise<LegalPagesResponse[]> {
  try {
    const results = await get("legalPages", { prompt: filter });
    return Array.isArray(results) ? results as LegalPagesResponse[] : [];
  } catch (error) {
    console.error(`Error getting LegalPages collection at path 'legalPages': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in LegalPages collection at legalPages
 
  Read Operation Details: Anyone can read legal pages. Used to display terms of service and privacy policy publicly on the site.
  
 */
export function subscribeAllLegalPages(callback: (data: LegalPagesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("legalPages", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as LegalPagesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to LegalPages collection at path '${"legalPages"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count LegalPages items in collection legalPages.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countLegalPages(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("legalPages", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting LegalPages at path 'legalPages': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on LegalPages items in collection legalPages.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateLegalPages(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("legalPages", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating LegalPages at path 'legalPages': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Admin or founder only. Can remove a legal page document.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteLegalPages(pageId: string): Promise<boolean> {
  try {
    const succeeded = await set(`legalPages/${pageId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting LegalPages: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for LegalPages for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteLegalPages(pageId: string): DocumentOperation {
  return { path: `legalPages/${pageId}`, document: null };
}

